<template>
  <div class="creator-lead">
    <!-- 主要内容区 -->
    <main class="max-w-7xl mx-auto px-4">
      <!-- 页面标题 -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-gray-800 mb-2">{{ $store.getters.t('creator_lead_dashboard') }}</h1>
        <p class="text-gray-600">
          <span class="text-green-600 font-semibold">{{ stats.leads }} {{ $store.getters.t('new_leads_added') }}</span> {{ $store.getters.t('added_to_talent_pool') }}
        </p>
      </div>

      <!-- 概览卡片 -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div class="bg-white rounded-lg shadow-md p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-gray-100 text-gray-600 mr-4">
              <i class="fas fa-users text-xl"></i>
            </div>
            <div>
              <p class="text-gray-500 text-sm">{{ $store.getters.t('leads') }}</p>
              <p class="text-2xl font-bold">{{ stats.leads }}</p>
            </div>
          </div>
        </div>
        <div class="bg-white rounded-lg shadow-md p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-gray-100 text-gray-600 mr-4">
              <i class="fas fa-arrow-up text-xl"></i>
            </div>
            <div>
              <p class="text-gray-500 text-sm">{{ $store.getters.t('rising') }}</p>
              <p class="text-2xl font-bold">{{ stats.rising }}</p>
            </div>
          </div>
        </div>
        <div class="bg-white rounded-lg shadow-md p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-gray-100 text-gray-600 mr-4">
              <i class="fas fa-handshake text-xl"></i>
            </div>
            <div>
              <p class="text-gray-500 text-sm">{{ $store.getters.t('affiliated') }}</p>
              <p class="text-2xl font-bold">{{ stats.affiliated }}</p>
            </div>
          </div>
        </div>
        <div class="bg-white rounded-lg shadow-md p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-gray-100 text-gray-600 mr-4">
              <i class="fas fa-times-circle text-xl"></i>
            </div>
            <div>
              <p class="text-gray-500 text-sm">{{ $store.getters.t('eliminated') }}</p>
              <p class="text-2xl font-bold">{{ stats.eliminated }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 筛选和搜索 -->
      <div class="bg-white rounded-lg shadow-md p-6 mb-6">
        <div class="flex flex-wrap items-center gap-4">
          <div class="flex items-center space-x-2">
            <label class="text-sm font-medium text-gray-700">{{ $store.getters.t('status_filter') }}:</label>
            <select 
              v-model="filters.status" 
              @change="loadCreators"
              class="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">{{ $store.getters.t('all_status') }}</option>
              <option value="待筛选">{{ $store.getters.t('pending_screening') }}</option>
              <option value="进入面试">{{ $store.getters.t('interview') }}</option>
              <option value="拒绝">{{ $store.getters.t('rejected_status') }}</option>
              <option value="录用">{{ $store.getters.t('hired_status') }}</option>
            </select>
          </div>
          
          <div class="flex items-center space-x-2">
            <label class="text-sm font-medium text-gray-700">{{ $store.getters.t('search') }}:</label>
            <input 
              v-model="filters.keyword" 
              @keyup.enter="loadCreators"
              type="text" 
              :placeholder="$store.getters.t('search_creator_placeholder')"
              class="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          
          <button 
            @click="loadCreators"
            class="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors"
          >
            <i class="fas fa-search mr-2"></i>
            {{ $store.getters.t('search') }}
          </button>
        </div>
      </div>

      <!-- 创作者卡片列表 -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <!-- 创作者卡片 -->
        <div 
          v-for="creator in creators" 
          :key="creator.id"
          class="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow" 
          @click="goToCreatorDetail(creator.id)"
        >
          <div class="flex items-start justify-between mb-4">
            <div class="flex items-center">
              <div class="w-16 h-16 bg-gray-200 rounded-full mr-4 flex items-center justify-center overflow-hidden">
                <img 
                  v-if="creator.avatarUrl" 
                  :src="creator.avatarUrl" 
                  :alt="creator.nickname || creator.preferredName"
                  class="w-full h-full object-cover"
                />
                <i v-else class="fas fa-user text-gray-400 text-xl"></i>
              </div>
              <div>
                <h3 class="text-lg font-bold">{{ creator.id }}</h3>
                <div class="flex items-center mt-1">
                  <span class="text-gray-600 text-sm">{{ creator.socialHandle }}</span>
                  <span class="text-gray-800 font-semibold ml-2">{{ creator.nickname || creator.preferredName }}</span>
                  <span class="ml-1">🚀</span>
                </div>
              </div>
            </div>
            <div class="flex space-x-2">
              <span class="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs">{{ $store.getters.t('live_creator') }}</span>
              <span class="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">{{ $store.getters.t('rising') }}</span>
              <!-- 状态标签 -->
              <span 
                :class="getStatusClass(creator.status)"
                class="px-2 py-1 rounded-full text-xs"
              >
                {{ creator.status }}
              </span>
            </div>
          </div>
          
          <div class="grid grid-cols-3 gap-4 mb-4">
            <div class="text-center">
              <p class="text-gray-500 text-sm">{{ $store.getters.t('videos') }}</p>
              <p class="font-bold">{{ creator.videos || 0 }}</p>
            </div>
            <div class="text-center">
              <p class="text-gray-500 text-sm">{{ $store.getters.t('followers') }}</p>
              <p class="font-bold">{{ formatNumber(creator.followers) }}</p>
            </div>
            <div class="text-center">
              <p class="text-gray-500 text-sm">{{ $store.getters.t('likes') }}</p>
              <p class="font-bold">{{ formatNumber(creator.likes) }}</p>
            </div>
          </div>
          
          <div class="text-sm text-gray-500">
            <p><strong>经验:</strong> {{ creator.experienceDesc || '无' }}</p>
            <p><strong>联系方式:</strong> {{ creator.contactInfo || '无' }}</p>
            <p><strong>创建时间:</strong> {{ formatDate(creator.createdAt) }}</p>
          </div>
        </div>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <p class="mt-2 text-gray-600">加载中...</p>
      </div>

      <!-- 错误状态 -->
      <div v-if="error" class="text-center py-8">
        <div class="text-red-600 mb-4">
          <i class="fas fa-exclamation-triangle text-2xl"></i>
        </div>
        <p class="text-gray-600">{{ error }}</p>
        <button @click="loadData" class="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
          重试
        </button>
      </div>

      <!-- 分页控件 -->
      <div v-if="!loading && creators.length > 0" class="bg-white rounded-lg shadow-md p-6">
        <div class="flex items-center justify-between">
          <div class="text-sm text-gray-600">
            {{ $store.getters.t('showing_records') }} {{ (currentPage - 1) * pageSize + 1 }} {{ $store.getters.t('to') }} {{ Math.min(currentPage * pageSize, totalRecords) }} {{ $store.getters.t('of') }} {{ totalRecords }} {{ $store.getters.t('records') }}
          </div>
          <div class="flex items-center space-x-2">
            <button 
              @click="goToFirst()"
              :disabled="currentPage === 1"
              class="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              首页
            </button>
            <button 
              @click="changePage(currentPage - 1)"
              :disabled="currentPage === 1"
              class="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ $store.getters.t('previous_page') }}
            </button>
            <span class="px-3 py-1 text-sm">{{ currentPage }} / {{ totalPages }}</span>
            <button 
              @click="changePage(currentPage + 1)"
              :disabled="currentPage === totalPages"
              class="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ $store.getters.t('next_page') }}
            </button>
            <button 
              @click="goToLast()"
              :disabled="currentPage === totalPages"
              class="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              尾页
            </button>
            <div class="flex items-center space-x-2 ml-2">
              <span class="text-sm text-gray-600">跳至</span>
              <input
                v-model.number="jumpPage"
                @keyup.enter="jumpToPage()"
                type="number"
                min="1"
                :max="totalPages"
                class="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <span class="text-sm text-gray-600">页</span>
              <button 
                @click="jumpToPage()"
                class="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700"
              >
                跳转
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import creatorService from '@/services/creatorService'

export default {
  name: 'CreatorLeadPage',
  data() {
    return {
      creators: [],
      stats: {
        leads: 0,
        rising: 0,
        affiliated: 0,
        eliminated: 0
      },
      loading: false,
      error: null,
      currentPage: 1,
      pageSize: 10,
      totalRecords: 0,
      totalPages: 0,
      jumpPage: null,
      filters: {
        status: '',
        keyword: ''
      }
    }
  },
  async created() {
    await this.loadData()
  },
  methods: {
    /**
     * 加载数据
     */
    async loadData() {
      this.loading = true
      this.error = null

      try {
        // 构建查询参数
        const params = {
          pageIndex: this.currentPage,
          pageSize: this.pageSize,
          status: this.filters.status || null,
          keyword: this.filters.keyword || null
        }

        // 获取创作者线索列表
        const leadsData = await creatorService.getCreatorLeads(params)
        const creators = leadsData.data || []

        // 获取详细数据
        const detailsData = await creatorService.batchGetCreatorDetails(creators)
        this.creators = detailsData.data || []
        
        // 设置分页信息
        this.totalRecords = leadsData.totalCount || creators.length
        this.totalPages = Math.ceil(this.totalRecords / this.pageSize)

        // 获取统计数据
        const statsData = await creatorService.getCreatorLeadsStats()
        this.stats = statsData

      } catch (error) {
        console.error('加载创作者线索数据失败:', error)
        this.error = '加载数据失败，请重试'
      } finally {
        this.loading = false
      }
    },
    
    /**
     * 加载创作者数据（用于筛选和搜索）
     */
    async loadCreators() {
      this.currentPage = 1
      await this.loadData()
    },
    
    /**
     * 切换页面
     */
    async changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
        await this.loadData()
      }
    },

    /**
     * 跳至首页
     */
    async goToFirst() {
      if (this.currentPage !== 1) {
        await this.changePage(1)
      }
    },

    /**
     * 跳至尾页
     */
    async goToLast() {
      if (this.currentPage !== this.totalPages) {
        await this.changePage(this.totalPages)
      }
    },

    /**
     * 数字定位到指定页
     */
    async jumpToPage() {
      const target = Number(this.jumpPage)
      if (!target || isNaN(target)) return
      const clamped = Math.max(1, Math.min(this.totalPages, target))
      this.jumpPage = clamped
      await this.changePage(clamped)
    },
    
    /**
     * 跳转到创作者详情页
     */
    goToCreatorDetail(id) {
      this.$router.push(`/creator-detail/${id}`)
    },
    
    /**
     * 格式化日期
     */
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    /**
     * 格式化数字
     */
    formatNumber(num) {
      if (num === null || num === undefined) return 'N/A'
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M'
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K'
      }
      return num.toLocaleString('zh-CN')
    },
    
    /**
     * 获取状态样式类
     */
    getStatusClass(status) {
      switch (status) {
        case '待处理':
          return 'bg-yellow-100 text-yellow-800'
        case '预跟踪':
          return 'bg-green-100 text-green-800'
        case '已签约':
          return 'bg-blue-100 text-blue-800'
        case '已淘汰':
          return 'bg-red-100 text-red-800'
        default:
          return 'bg-gray-100 text-gray-800'
      }
    }
  }
}
</script>

<style scoped>
.creator-lead {
  min-height: calc(100vh - 64px);
}

.grid {
  display: grid;
}

.grid-cols-1 {
  grid-template-columns: repeat(1, minmax(0, 1fr));
}

@media (min-width: 768px) {
  .md\:grid-cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .lg\:grid-cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  
  .lg\:grid-cols-4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.gap-6 {
  gap: 1.5rem;
}

.mb-8 {
  margin-bottom: 2rem;
}

.mb-4 {
  margin-bottom: 1rem;
}

.mt-1 {
  margin-top: 0.25rem;
}

.ml-1 {
  margin-left: 0.25rem;
}

.ml-2 {
  margin-left: 0.5rem;
}

.mr-4 {
  margin-right: 1rem;
}

.p-6 {
  padding: 1.5rem;
}

.p-3 {
  padding: 0.75rem;
}

.px-4 {
  padding-left: 1rem;
  padding-right: 1rem;
}

.py-8 {
  padding-top: 2rem;
  padding-bottom: 2rem;
}

.py-2 {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.px-2 {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.py-1 {
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}

.text-3xl {
  font-size: 1.875rem;
}

.text-2xl {
  font-size: 1.5rem;
}

.text-xl {
  font-size: 1.25rem;
}

.text-lg {
  font-size: 1.125rem;
}

.text-sm {
  font-size: 0.875rem;
}

.text-xs {
  font-size: 0.75rem;
}

.font-bold {
  font-weight: 700;
}

.font-semibold {
  font-weight: 600;
}

.text-gray-800 {
  color: #1f2937;
}

.text-gray-600 {
  color: #4b5563;
}

.text-gray-500 {
  color: #6b7280;
}

.text-green-600 {
  color: #16a34a;
}

.text-gray-400 {
  color: #9ca3af;
}

.text-yellow-800 {
  color: #92400e;
}

.text-blue-800 {
  color: #1e40af;
}

.text-green-800 {
  color: #166534;
}

.text-purple-800 {
  color: #5b21b6;
}

.text-yellow-800 {
  color: #9a3412;
}

.text-red-800 {
  color: #991b1b;
}

.bg-white {
  background-color: #ffffff;
}

.bg-gray-100 {
  background-color: #f3f4f6;
}

.bg-yellow-100 {
  background-color: #fef3c7;
}

.bg-blue-100 {
  background-color: #dbeafe;
}

.bg-green-100 {
  background-color: #dcfce7;
}

.bg-purple-100 {
  background-color: #f3e8ff;
}

.bg-yellow-100 {
  background-color: #fed7aa;
}

.bg-red-100 {
  background-color: #fee2e2;
}

.rounded-lg {
  border-radius: 0.5rem;
}

.rounded-full {
  border-radius: 9999px;
}

.shadow-md {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.hover\:shadow-lg:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.transition-shadow {
  transition: box-shadow 0.2s ease-in-out;
}

.cursor-pointer {
  cursor: pointer;
}

.w-16 {
  width: 4rem;
}

.h-16 {
  height: 4rem;
}

.flex {
  display: flex;
}

.items-center {
  align-items: center;
}

.items-start {
  align-items: flex-start;
}

.justify-between {
  justify-content: space-between;
}

.space-x-2 > * + * {
  margin-left: 0.5rem;
}

.text-center {
  text-align: center;
}
</style> 